import React, { type ChangeEvent } from "react";
import {
  Checkbox,
  FormControlLabel,
  InputAdornment,
  Radio,
  RadioGroup,
  Switch,
} from "@mui/material";
import Autocomplete from "@components/Autocomplete/Autocomplete";
import DatePicker from "@components/DatePicker/DatePicker";
import { DocumentUpload } from "@components/FileUpload/FileUpload";
import type { FileUploadConditions } from "@components/FileUpload/FileUpload.types";
import type { UploadEntry } from "@components/FileUpload/uploadStore";
import CustomFormLabel from "@components/FormItem/FormItem.styles";
import Input from "@components/Input/Input";
import { TextField as StyledTextField } from "@components/Input/Input.styles";
import { MultipleSelect } from "@components/Select/MultipleSelect";
import Select from "@components/Select/Select";
import { t } from "i18next";
import { NumericFormat } from "react-number-format";

type FormItemType =
  | "text"
  | "number"
  | "select"
  | "multiple-select"
  | "checkbox"
  | "radio"
  | "switch"
  | "date-picker"
  | "autocomplete"
  | "file"
  | "textarea";

type DateConstraint = "past" | "future" | "not-past" | "not-future";

interface Option {
  label: string;
  value: string | number | boolean;
  disabled?: boolean;
}

type SlotProps = {
  htmlInput?: React.InputHTMLAttributes<HTMLInputElement>;
  input?: Record<string, unknown>;
};

const buildInputSlotProps = (slotProps?: SlotProps, adornment?: React.ReactNode) => {
  if (!adornment) return slotProps;

  return {
    ...slotProps,
    input: {
      ...slotProps?.input,
      endAdornment: <InputAdornment position="end">{adornment}</InputAdornment>,
    },
  };
};

interface FormItemFieldRendererProps {
  type: FormItemType;
  name: string;
  label?: string;
  labelId?: string;
  testId: string;
  dir?: string;
  disabled: boolean;
  required: boolean;
  options: Option[];
  field: any;
  error?: string;
  validationLabel?: string;
  defaultValue?: string | number | boolean | null;
  maxDigits?: number;
  decimals: number;
  canBeginWithZero: boolean;
  adornment?: React.ReactNode;
  thousandSeparator?: boolean;
  disableNativeNumberPattern?: boolean;
  maxChars?: number;
  minRows: number;
  // DFT-0083: opt-in, defaults to undefined (identical to today's unbounded-growth behavior)
  // for every caller except those that explicitly set it - see TextAreaField below.
  maxRows?: number;
  slotProps?: SlotProps;
  horizontalRadioButtons?: boolean;
  timePicker?: boolean;
  quickCapture?: boolean;
  dateConstraint?: DateConstraint;
  minDate?: Date;
  maxDate?: Date;
  datePickerSlotProps?: any;
  combinedInput: boolean;
  forceHighZIndexPopper: boolean;
  maxFiles?: number;
  fileVariant?: "button";
  uploadConditions?: FileUploadConditions;
  allowedFileExtensions?: string[];
  files?: UploadEntry[];
  setFiles?: (files: UploadEntry[]) => void;
  uploadDescription?: string;
  allowReplaceExisting?: boolean;
  uploadStoreKey?: string;
  fileValidation?: (files: File[]) => Promise<string | null | void> | string | null | void;
  notSuccess: boolean;
  autocompleteValue?: Option | null;
  inputLabel?: string;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  formatOnChange?: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onReject?: () => void;
  onUpload?: (key: string, files: File[]) => void;
  onRemoveFile?: (name: string, index: number) => void;
  clearError: () => void;
  setError: (message: string, type?: string) => void;
  triggerField: () => void;
  onFieldChangeImmediate?: (name: string, value: unknown) => void;
  onMaxCharAttempt?: () => void;
  onCommitValue: (value: unknown) => unknown;
  onBlurTrim: (e?: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  validateDrop?: (selectedCount: number) => string | null;
}

const isPrintableKey = (e: React.KeyboardEvent) =>
  e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;

const collapseRepeatedSpaces = (value: string) => value.replace(/ {2,}/g, " ");

const toSafeDate = (raw: unknown): Date | null => {
  if (raw == null) return null;
  if (raw instanceof Date) return isNaN(raw.getTime()) ? null : raw;
  if (typeof raw === "string") {
    const s = raw.trim().toLowerCase();
    if (!s || s === "null" || s === "undefined") return null;
    const d = new Date(raw);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof raw === "number") {
    const d = new Date(raw);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
};

const buildPartialNumberPattern = (decimals: number) =>
  decimals && decimals > 0 ? /^\d*(\.\d*)?$/ : /^\d*$/;

const SelectField = ({
  field,
  options,
  testId,
  label,
  dir,
  disabled,
  required,
  triggerField,
  onCommitValue,
  error,
}: FormItemFieldRendererProps) => (
  <Select
    {...field}
    dir={dir}
    error={!!error}
    onBlur={field.onBlur}
    onClose={() => {
      if (required) triggerField();
    }}
    onChange={(e) => onCommitValue((e as { target?: { value?: unknown } })?.target?.value ?? e)}
    options={options}
    testId={testId}
    placeholder={label}
    disabled={disabled}
  />
);

const MultipleSelectField = ({
  field,
  options,
  testId,
  label,
  disabled,
  error,
  required,
  triggerField,
  onCommitValue,
}: FormItemFieldRendererProps) => (
  <MultipleSelect
    options={options.map((option) => ({
      label: option.label,
      value: String(option.value),
      disabled: option.disabled,
    }))}
    option={Array.isArray(field.value) ? field.value.map(String) : []}
    setOption={onCommitValue}
    error={!!error}
    placeholder={label}
    testId={testId}
    disabled={disabled}
    id={testId}
    onBlur={field.onBlur}
    onClose={() => {
      if (required) triggerField();
    }}
  />
);

const CheckboxField = ({ field, label, disabled, onCommitValue }: FormItemFieldRendererProps) => (
  <FormControlLabel
    control={
      <Checkbox
        {...field}
        checked={!!field.value}
        onChange={(e) => onCommitValue((e as React.ChangeEvent<HTMLInputElement>).target.checked)}
      />
    }
    label={<CustomFormLabel as="span">{label}</CustomFormLabel>}
    disabled={disabled}
  />
);

const RadioField = ({
  field,
  options,
  horizontalRadioButtons,
  disabled,
  testId,
  onCommitValue,
}: FormItemFieldRendererProps) => (
  <RadioGroup
    row={horizontalRadioButtons}
    {...field}
    value={field.value}
    onChange={(e) => {
      const raw = (e as React.ChangeEvent<HTMLInputElement>).target.value;
      const isBoolean = raw === "true" || raw === "false";
      onCommitValue(isBoolean ? raw === "true" : raw);
    }}
  >
    {options.map((opt) => (
      <FormControlLabel
        key={String(opt.value)}
        value={opt.value}
        control={<Radio data-testid={`${testId}-${String(opt.value)}`} />}
        label={<CustomFormLabel as="span">{opt.label}</CustomFormLabel>}
        disabled={disabled}
      />
    ))}
  </RadioGroup>
);

const SwitchField = ({ field, label, disabled, onCommitValue }: FormItemFieldRendererProps) => (
  <FormControlLabel
    control={
      <Switch
        {...field}
        checked={!!field.value}
        onChange={(e) => onCommitValue((e as React.ChangeEvent<HTMLInputElement>).target.checked)}
      />
    }
    label={label}
    disabled={disabled}
  />
);

const DatePickerField = ({
  field,
  error,
  timePicker,
  quickCapture,
  dateConstraint,
  combinedInput,
  minDate,
  maxDate,
  disabled,
  triggerField,
  testId,
  datePickerSlotProps,
  forceHighZIndexPopper,
  name,
  onFieldChangeImmediate,
}: FormItemFieldRendererProps) => {
  const safeValue = toSafeDate(field.value);
  const effectiveSlotProps = {
    ...datePickerSlotProps,
    popper: {
      ...datePickerSlotProps?.popper,
      disablePortal: false,
      sx: {
        zIndex: forceHighZIndexPopper ? 9999 : 1402,
        ...datePickerSlotProps?.popper?.sx,
      },
    },
  };
  return (
    <DatePicker
      value={safeValue}
      hasError={!!error}
      timePicker={timePicker}
      quickCapture={quickCapture}
      dateConstraint={dateConstraint}
      combinedInput={combinedInput}
      minDate={minDate}
      maxDate={maxDate}
      disabled={disabled}
      onChange={(date) => {
        const validDate = date instanceof Date && !isNaN(date.getTime()) ? date : null;
        field.onChange(validDate);
        onFieldChangeImmediate?.(name, validDate);
        triggerField();
      }}
      onClose={() => {
        if (safeValue !== null) triggerField();
      }}
      testId={testId}
      slotProps={effectiveSlotProps}
    />
  );
};

const FileField = ({
  testId,
  label,
  labelId,
  field,
  triggerField,
  onUpload,
  onReject,
  onRemoveFile,
  name,
  maxFiles,
  allowedFileExtensions,
  uploadConditions,
  fileVariant,
  files,
  setFiles,
  uploadDescription,
  allowReplaceExisting,
  uploadStoreKey,
  fileValidation,
  notSuccess,
  error,
  setError,
  clearError,
  disabled,
  validateDrop,
}: FormItemFieldRendererProps) => {
  const allowedExtensionsToUse =
    allowedFileExtensions && allowedFileExtensions.length
      ? allowedFileExtensions
      : (uploadConditions?.format ?? []);

  if (allowedExtensionsToUse.length === 0) {
    throw new Error(`FormItem: missing allowedFileExtensions for file field "${name}"`);
  }

  const resolvedMaxFiles = maxFiles ?? 1;

  return (
    <DocumentUpload
      testId={testId}
      label={label}
      labelId={labelId}
      onUpload={(uploadedFiles: File[]) => {
        field.onChange(uploadedFiles);
        if (!notSuccess) triggerField();
        void onUpload?.(name, uploadedFiles);
      }}
      onReject={onReject}
      onRemoveFile={(entries: UploadEntry[], index) => {
        const remainingFiles = entries.filter((entry): entry is File => entry instanceof File);
        field.onChange(remainingFiles);
        triggerField();
        onRemoveFile?.(name, index);
      }}
      name={name}
      uploadStoreKey={uploadStoreKey}
      maxFiles={resolvedMaxFiles}
      allowedExtensions={allowedExtensionsToUse}
      variant={fileVariant}
      conditions={uploadConditions}
      files={files ?? []}
      setFiles={setFiles ?? (() => {})}
      disabled={disabled}
      allowReplaceExisting={allowReplaceExisting}
      isError={!!error}
      uploadDescription={uploadDescription}
      validateFiles={fileValidation}
      onValidationResult={(message) => {
        if (message) {
          setError(message, "manual");
          return;
        }
        clearError();
      }}
      validateDrop={validateDrop}
    />
  );
};

const AutocompleteField = ({
  field,
  autocompleteValue,
  options,
  testId,
  disabled,
  label,
  dir,
  onCommitValue,
  error,
}: FormItemFieldRendererProps) => {
  const selectedOption =
    autocompleteValue !== undefined
      ? autocompleteValue
      : options.find((option) => option.value === field.value) || null;

  return (
    <Autocomplete
      {...field}
      dir={dir}
      error={!!error}
      placeholder={label}
      value={selectedOption}
      options={options}
      testId={testId}
      disabled={disabled}
      onChange={(option) => onCommitValue((option as { value?: unknown } | null)?.value ?? "")}
    />
  );
};

const NumberField = ({
  field,
  disabled,
  dir,
  decimals,
  canBeginWithZero,
  disableNativeNumberPattern = false,
  maxDigits,
  label,
  validationLabel,
  clearError,
  setError,
  onCommitValue,
  slotProps,
  testId,
  error,
  adornment,
  thousandSeparator,
}: FormItemFieldRendererProps) => {
  const partialNumberPattern = buildPartialNumberPattern(decimals);
  const validateNumberValue = (value: string) => {
    if (!value) {
      clearError();
      return true;
    }

    const numberOfDigits = value.split(".")[0].length;
    const numberOfDecimals = value.split(".")[1]?.length || 0;

    if (!canBeginWithZero) {
      const startsWithZero = value.startsWith("0") && !value.startsWith("0.");
      if (startsWithZero && value.length > 1) {
        setError(
          t("form-validation.cannot-begin-with-zero", {
            label,
          }),
        );
        return false;
      }
    }

    if (typeof maxDigits === "number" && numberOfDigits > maxDigits) {
      setError(
        t("form-validation.max-length-digits", {
          label: validationLabel || label,
          maxDigits,
        }),
        "maxDigits",
      );
      return false;
    }

    if (decimals && numberOfDecimals > decimals) {
      setError(
        t("form-validation.max-length-decimals", {
          label: validationLabel || label,
          maxDecimals: decimals,
        }),
        "maxDecimals",
      );
      return false;
    }

    clearError();
    return true;
  };

  if (thousandSeparator) {
    return (
      <NumericFormat
        value={field.value ?? ""}
        valueIsNumericString
        customInput={StyledTextField}
        thousandSeparator=","
        allowLeadingZeros={canBeginWithZero}
        allowNegative={false}
        placeholder={label}
        disabled={disabled}
        error={!!error}
        onBlur={field.onBlur}
        onValueChange={({ value }) => {
          if (validateNumberValue(value)) {
            onCommitValue(value);
          }
        }}
        isAllowed={({ value }) => validateNumberValue(value)}
        slotProps={{
          ...buildInputSlotProps(slotProps, adornment),
          htmlInput: {
            "min": "0",
            "step": decimals && decimals > 0 ? (1 / Math.pow(10, decimals)).toString() : "1",
            "data-testid": testId,
            ...slotProps?.htmlInput,
          },
        }}
      />
    );
  }

  return (
    <Input
      {...field}
      dir={dir}
      error={!!error}
      placeholder={label}
      disabled={disabled}
      onKeyDown={(e) => {
        const invalidKeys = ["e", "E", "+", "-"];
        if (!(decimals && decimals > 0)) invalidKeys.push(".");
        if (invalidKeys.includes(e.key)) e.preventDefault();
      }}
      onBeforeInput={(e) => {
        const nativeEvent = e.nativeEvent as InputEvent;
        const nextChunk = nativeEvent.data;
        if (!nextChunk) return;

        const input = e.target as HTMLInputElement;
        const start = input.selectionStart ?? input.value.length;
        const end = input.selectionEnd ?? input.value.length;
        const nextValue = `${input.value.slice(0, start)}${nextChunk}${input.value.slice(end)}`;

        if (!partialNumberPattern.test(nextValue)) {
          e.preventDefault();
        }
      }}
      onChange={(e) => {
        const value = e.target.value;
        const currentValue = String(field.value ?? "");

        if (value.length < currentValue.length) {
          onCommitValue(value);
          return;
        }

        if (!partialNumberPattern.test(value)) return;
        if (!validateNumberValue(value)) return;
        onCommitValue(value);
      }}
      onPaste={(e) => {
        const paste = e.clipboardData.getData("text");
        const regex =
          decimals && decimals > 0 ? new RegExp(`^\\d+(\\.\\d{0,${decimals}})?$`) : /^\d+$/;
        if (!regex.test(paste)) e.preventDefault();
        if (!validateNumberValue(paste)) e.preventDefault();
      }}
      slotProps={{
        ...buildInputSlotProps(slotProps, adornment),
        htmlInput: {
          type: "text",
          inputMode: decimals && decimals > 0 ? "decimal" : "numeric",
          pattern: disableNativeNumberPattern
            ? undefined
            : decimals && decimals > 0
              ? "^\\d*(\\.\\d*)?$"
              : "^\\d*$",
          autoComplete: "off",
          ...slotProps?.htmlInput,
        },
      }}
      testId={testId}
    />
  );
};

const TextAreaField = ({
  field,
  dir,
  minRows,
  maxRows,
  onBlurTrim,
  maxChars,
  label,
  onCommitValue,
  slotProps,
  testId,
  disabled,
  error,
  onMaxCharAttempt,
}: FormItemFieldRendererProps) => (
  <Input
    {...field}
    dir={dir}
    error={!!error}
    placeholder={label}
    multiline
    minRows={minRows}
    maxRows={maxRows}
    fullWidth
    variant="outlined"
    onBlur={onBlurTrim}
    onKeyDown={(e) => {
      if (!maxChars) return;
      if (!isPrintableKey(e)) return;

      const current = String(field.value ?? "");
      if (current.length >= maxChars) {
        onMaxCharAttempt?.();
      }
    }}
    onChange={(e) => {
      const value = (e as React.ChangeEvent<HTMLTextAreaElement>).target.value;
      onCommitValue(value);
    }}
    slotProps={{
      root: { className: "rounded-lg border border-gray-500 text-sm px-4 py-2" },
      htmlInput: { ...slotProps?.htmlInput, maxLength: maxChars },
      ...slotProps,
    }}
    sx={{
      "& .MuiInputBase-root": {
        borderRadius: "8px",
        borderColor: "#6B7280",
        padding: "12px",
        fontSize: "14px",
      },
      "& textarea": { padding: 0 },
    }}
    testId={testId}
    disabled={disabled}
  />
);

const TextField = ({
  field,
  disabled,
  dir,
  inputLabel,
  label,
  onBlurTrim,
  onFocus,
  maxChars,
  adornment,
  formatOnChange,
  onCommitValue,
  slotProps,
  testId,
  error,
  onMaxCharAttempt,
}: FormItemFieldRendererProps) => {
  const handleBlur = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const sanitizedValue = collapseRepeatedSpaces(event.target.value);

    if (sanitizedValue !== event.target.value) {
      event.target.value = sanitizedValue;
      onCommitValue(sanitizedValue);
    }

    onBlurTrim(event);
  };

  return (
    <Input
      {...field}
      dir={dir}
      error={!!error}
      disabled={disabled}
      placeholder={inputLabel || label}
      onBlur={handleBlur}
      onFocus={onFocus}
      onKeyDown={(e) => {
        if (!maxChars) return;
        if (!isPrintableKey(e)) return;

        const current = String(field.value ?? "");
        if (current.length >= maxChars) {
          onMaxCharAttempt?.();
        }
      }}
      onChange={(e) => {
        if (formatOnChange) formatOnChange(e);
        const input = (e as React.ChangeEvent<HTMLInputElement>).currentTarget;
        const value = input.value;
        const selectionStart = input.selectionStart;
        const selectionEnd = input.selectionEnd;
        const committedValue = onCommitValue(value);

        if (
          typeof committedValue === "string" &&
          committedValue !== value &&
          selectionStart !== null &&
          selectionEnd !== null
        ) {
          input.value = committedValue;
          input.setSelectionRange(selectionStart, selectionEnd);
        }
      }}
      slotProps={{
        ...buildInputSlotProps(slotProps, adornment),
        htmlInput: { ...slotProps?.htmlInput, maxLength: maxChars },
      }}
      testId={testId}
    />
  );
};

const FormItemFieldRenderer = (props: FormItemFieldRendererProps) => {
  switch (props.type) {
    case "select":
      return <SelectField {...props} />;
    case "multiple-select":
      return <MultipleSelectField {...props} />;
    case "checkbox":
      return <CheckboxField {...props} />;
    case "radio":
      return <RadioField {...props} />;
    case "switch":
      return <SwitchField {...props} />;
    case "date-picker":
      return <DatePickerField {...props} />;
    case "file":
      return <FileField {...props} />;
    case "autocomplete":
      return <AutocompleteField {...props} />;
    case "number":
      return <NumberField {...props} />;
    case "textarea":
      return <TextAreaField {...props} />;
    default:
      return <TextField {...props} />;
  }
};

export type { DateConstraint, FormItemType, Option };
export default FormItemFieldRenderer;
